import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import { recordApiFailure } from '@/lib/sentry';

/**
 * Empty by design: the API is served by the same Worker as this bundle, so every
 * request path already starts with `/api` and a relative URL hits the right place.
 *
 * This used to be an absolute `VITE_API_URL` baked in at build time, because the
 * frontend was a separate Pages project on a different origin. That is gone —
 * there is one Worker now (apps/api/wrangler.toml), which is why the API needs no
 * CORS grant and no rebuild when it moves to a custom domain.
 *
 * In local dev the two are still split across ports, and Vite's `/api` proxy
 * (vite.config.ts) forwards to the API so relative URLs work there too.
 *
 * `VITE_API_URL` is still read, but only usefully for another *local* API — a
 * second checkout, a different port. It cannot point at the deployed Worker:
 * that API allows exactly one CORS origin, its own (`FRONTEND_URL`, app.ts), so
 * a cross-origin call from localhost is answered with an origin the browser
 * rejects and every request fails as if the API were down.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true,
});

/**
 * Endpoints that establish *who you are*, rather than acting as someone already
 * known.
 *
 * The API resolves a sign-in within the school named by `X-School-ID` (AR-21).
 * Attaching this header from the persisted session would scope it to whoever was
 * signed in last: the login page has no redirect-if-authenticated guard, so
 * someone still holding a school-A session who comes back from the provider as a
 * school-B person would be looked up in school A and match nothing.
 *
 * A stale session must not narrow a fresh sign-in, so these paths never carry it.
 * `/auth/login` is gone as an endpoint - the credential is the provider's now -
 * and what is left on this page is the half of sign-in that happens after the
 * person has already been verified.
 */
const IDENTITY_PATHS = [
  '/auth/select-school',
  '/auth/selection-schools',
  '/signup',
  '/sso/',
];

function establishesIdentity(url: string | undefined): boolean {
  return url !== undefined && IDENTITY_PATHS.some((path) => url.includes(path));
}

// Request interceptor to add auth token and school ID
api.interceptors.request.use((config) => {
  const { token, user } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (user?.schoolId && !establishesIdentity(config.url)) {
    config.headers['X-School-ID'] = user.schoolId;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Every failed call leaves a breadcrumb, so whatever happens next arrives
    // with the trail that led to it. Deliberately not an event of its own: a
    // 404 the app handled is context, and one issue per failed request would
    // bury the crashes.
    //
    // `errorId` is the id the API put in its own 500 body
    // (apps/api/src/app.ts). Carrying it here is what joins a browser event to
    // the server event that caused it — otherwise the two sides of one failure
    // are two unrelated issues.
    recordApiFailure({
      method: error.config?.method ?? 'get',
      url: error.config?.url ?? 'unknown',
      status: error.response?.status,
      errorId: error.response?.data?.errorId,
    });

    // Don't redirect on 401 for the endpoints the login page owns - it handles
    // them itself, and bouncing would throw away the school picker mid-choice.
    //
    // This used to name `/auth/login`, which no longer exists as an endpoint:
    // the credential is the provider's now, and what is left on this page is the
    // half of sign-in that happens after the person is already verified.
    const isSignInRequest = ['/auth/select-school', '/auth/selection-schools'].some(
      (path) => error.config?.url?.includes(path)
    );
    if (error.response?.status === 401 && !isSignInRequest) {
      useAuthStore.getState().logout();
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export default api;
